import React, { useEffect, useRef, useState } from "react";
import { MapPin, LocateFixed, Loader2, Search, Home, Briefcase, Clock } from "lucide-react";
import { Input } from "../ui/input";
import { KOHIMA_PLACES } from "../../lib/constants";
import { mapsApi, addressApi } from "../../lib/api";
import { toast } from "sonner";

// Address search via Google Places Autocomplete (through our backend). Returns {name, place_id}.
async function geocode(q, session) {
  try {
    const r = await mapsApi.autocomplete(q, session);
    return (r.suggestions || []).map((s) => ({ name: s.text, place_id: s.place_id }));
  } catch { return []; }
}

export async function reverseGeocode(lat, lng) {
  // Google-only reverse geocoding via our backend (Geocoding API, 10s server timeout).
  try {
    const d = await mapsApi.reverse(lat, lng);
    return d?.address || null;
  } catch { return null; }
}

export function locateMe(timeout = 8000) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("unsupported"));
    const ok = (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    // Quick fix: accept a recent cached position instantly (maximumAge), then a relaxed retry.
    navigator.geolocation.getCurrentPosition(
      ok,
      () => navigator.geolocation.getCurrentPosition(ok, reject, { enableHighAccuracy: false, timeout: 15000, maximumAge: 120000 }),
      { enableHighAccuracy: false, timeout, maximumAge: 60000 },
    );
  });
}

export default function PlaceInput({ label, labelExtra, value, onChange, placeholder, allowCurrent = false, showQuick = true, near = null, testId }) {
  const [query, setQuery] = useState(value?.name || "");
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState([]);
  const [popular, setPopular] = useState([]);
  const [saved, setSaved] = useState([]);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);
  const session = useRef(Math.random().toString(36).slice(2));

  useEffect(() => setQuery(value?.name || ""), [value?.name]);

  useEffect(() => {
    if (!showQuick) return;
    addressApi.list().then((l) => setSaved((l || []).filter((a) => Number.isFinite(a.lat)).slice(0, 4))).catch(() => {});
    addressApi.recentPlaces(4).then((p) => setRecent(p || [])).catch(() => {});
  }, [showQuick]);

  useEffect(() => {
    if (near?.lat == null) return;
    mapsApi.popularNearby(near.lat, near.lng).then(setPopular).catch(() => setPopular([]));
  }, [near?.lat, near?.lng]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const nearList = (!query.trim() && popular.length) ? popular.slice(0, 6).map((p) => ({ name: p.name, lat: p.lat, lng: p.lng, _popular: true })) : null;
  const local = nearList || (query.trim().length ? KOHIMA_PLACES.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 5) : KOHIMA_PLACES.slice(0, 6));

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(true);
    if (value) onChange(null);
    clearTimeout(timer.current);
    if (q.trim().length < 3) { setRemote([]); return; }
    timer.current = setTimeout(async () => {
      setBusy(true);
      try { setRemote(await geocode(q, session.current)); } catch { setRemote([]); } finally { setBusy(false); }
    }, 500);
  };

  const pick = async (p) => {
    // Local landmarks already carry lat/lng; Google autocomplete results resolve on pick.
    if (p.place_id && !Number.isFinite(p.lat)) {
      setQuery(p.name);
      setOpen(false);
      setBusy(true);
      try {
        const d = await mapsApi.place(p.place_id);
        session.current = Math.random().toString(36).slice(2);
        if (Number.isFinite(d.lat) && Number.isFinite(d.lng)) onChange({ name: d.address || p.name, lat: d.lat, lng: d.lng });
      } catch { /* ignore */ } finally { setBusy(false); }
      return;
    }
    onChange(p);
    setQuery(p.name);
    setOpen(false);
  };

  const useCurrent = () => {
    if (!navigator.geolocation) { toast.error("Location not supported on this device"); return; }
    setOpen(false);
    setBusy(true);
    locateMe()
      .then(async (c) => { pick({ name: (await reverseGeocode(c.lat, c.lng)) || "Current location", ...c }); })
      .catch((err) => toast.error(err?.code === 1 ? "Location permission denied — enable it in your browser settings" : "Couldn't detect your location. Try again or type your address"))
      .finally(() => setBusy(false));
  };

  const [highlight, setHighlight] = useState(0);
  const options = [...local, ...remote];

  const onKeyDown = (e) => {
    if (!open || options.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(options.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(options[highlight] || options[0]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={wrap} className="relative">
      {label && <div className="mb-1.5 flex items-center justify-between gap-2"><label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted">{label}</label>{labelExtra}</div>}
      <div className="relative">
        <MapPin className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${value ? "text-graphite" : "text-ink-muted"}`} />
        <Input value={query} onChange={(e) => { setHighlight(0); handleChange(e); }} onFocus={() => setOpen(true)} onKeyDown={onKeyDown} placeholder={placeholder} className="h-11 bg-white pl-9 pr-10" data-testid={testId} autoComplete="off" role="combobox" aria-expanded={open} />
        <span className="absolute right-2 top-1/2 -translate-y-1/2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin text-ink-muted" /> : <Search className="h-4 w-4 text-ink-muted" />}
        </span>
      </div>
      {open && (
        <ul className="absolute z-[1000] mt-1.5 max-h-72 w-full overflow-y-auto rounded-xl border border-surface-300 bg-white p-1.5 shadow-xl shadow-ink/10 kt-scrollbar" data-testid={`${testId}-suggestions`} role="listbox">
          <li>
            <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={useCurrent} className="flex w-full items-center gap-2 rounded-lg border-b border-surface-200 px-2.5 py-2.5 text-left text-sm font-semibold text-brand hover:bg-brand-50" data-testid={`${testId}-locate-option`}>
              <LocateFixed className="h-4 w-4 shrink-0" /> Use my current location
            </button>
          </li>
          {showQuick && !query.trim() && saved.length > 0 && <li className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Saved</li>}
          {showQuick && !query.trim() && saved.map((a) => {
            const Ic = (a.label || "").toLowerCase() === "home" ? Home : (a.label || "").toLowerCase() === "work" ? Briefcase : MapPin;
            return (
              <li key={`sv-${a.address_id}`}><button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange({ name: a.address, lat: a.lat, lng: a.lng }); setQuery(a.address); setOpen(false); }} className="flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-ink hover:bg-surface-100" data-testid={`${testId}-saved-${a.address_id}`}><Ic className="mt-0.5 h-4 w-4 shrink-0 text-brand" /><span><span className="font-semibold">{a.label}</span> · {a.name || a.address}</span></button></li>
            );
          })}
          {showQuick && !query.trim() && recent.length > 0 && <li className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Recent</li>}
          {showQuick && !query.trim() && recent.map((r, i) => (
            <li key={`rc-${i}`}><button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange({ name: r.address, lat: r.lat, lng: r.lng }); setQuery(r.address); setOpen(false); }} className="flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-ink hover:bg-surface-100" data-testid={`${testId}-recent-${i}`}><Clock className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" /><span className="line-clamp-2">{r.address}</span></button></li>
          ))}
          {local.length > 0 && <li className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">{nearList ? "Popular near you" : "Popular places"}</li>}
          {local.map((p, i) => (
            <li key={p.name} role="option" aria-selected={highlight === i}><button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(p)} className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-ink hover:bg-surface-100 ${highlight === i ? "bg-surface-100" : ""}`} data-testid={`${testId}-option-${i}`}><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{p.name}</button></li>
          ))}
          {remote.length > 0 && <li className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Search results</li>}
          {remote.map((p, j) => {
            const i = local.length + j;
            return (
              <li key={p.place_id || `${p.lat}-${p.lng}`} role="option" aria-selected={highlight === i}><button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(p)} className={`flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left text-sm text-ink hover:bg-surface-100 ${highlight === i ? "bg-surface-100" : ""}`} data-testid={`${testId}-option-${i}`}><Search className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" /><span className="line-clamp-2">{p.name}</span></button></li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
